import React from "react";
import useReportFormat from "../useReportFormat";

const cardBase = {
  background: "white",
  borderRadius: 12,
  padding: 24,
  boxShadow: "0 4px 20px -4px rgba(0,0,0,0.05)",
  border: "1px solid #f0f4ff",
};

const labelStyle = {
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "#64748b",
  marginBottom: 0,
};

const amountStyle = {
  fontSize: "1.75rem",
  fontWeight: 800,
  color: "#002045",
  letterSpacing: "-0.03em",
  margin: "8px 0",
};

const sectionLabel = {
  fontSize: "0.6rem",
  fontWeight: 800,
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  color: "#94a3b8",
  marginBottom: 12,
  marginTop: 0,
  paddingBottom: 8,
  borderBottom: "1px solid #f1f5f9",
};

const RevenueKpiCards = ({
  totalListing = 0,
  totalSold = 0,
  totalListingSold = 0,
  totalCollected = 0,
  remainingToSell = 0,
  soldUnits = 0,
  totalUnits = 0,
}) => {
  const formatCurrency = useReportFormat();
  const soldPct = totalListing > 0 ? Math.round((totalSold / totalListing) * 100) : 0;
  const collectedPct = totalSold > 0 ? Math.round((totalCollected / totalSold) * 100) : 0;
  const gain = totalListingSold > 0 ? totalSold - totalListingSold : null;
  const gainPct = totalListingSold > 0 ? ((totalSold - totalListingSold) / totalListingSold) * 100 : 0;
  const gainPositive = gain === null ? null : gain >= 0;

  return (
    <div style={{ marginBottom: 28 }}>
      <p style={sectionLabel}>Revenue</p>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
          gap: 24,
        }}
      >
        {/* Total Listing */}
        <div style={cardBase}>
          <p style={labelStyle}>Total Listing Value</p>
          <div style={amountStyle}>{formatCurrency(totalListing)}</div>
          <div style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, marginTop: 4 }}>
            {totalUnits} unit{totalUnits !== 1 ? "s" : ""}
          </div>
        </div>

        {/* Total Sold */}
        <div style={{ ...cardBase, borderLeft: "4px solid #059669" }}>
          <p style={labelStyle}>Total Sold</p>
          <div style={amountStyle}>{formatCurrency(totalSold)}</div>
          <div style={{ height: 4, background: "#e2e8f0", borderRadius: 99, overflow: "hidden", marginTop: 4 }}>
            <div
              style={{
                height: "100%",
                width: `${Math.min(soldPct, 100)}%`,
                background: "#059669",
                borderRadius: 99,
                transition: "width 0.4s ease",
              }}
            />
          </div>
          <p style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, marginTop: 6, marginBottom: 0 }}>
            {soldUnits}/{totalUnits} units · {soldPct}% of listing
          </p>
        </div>

        {/* Price Gain */}
        {/* {gain !== null && (
          <div style={{ ...cardBase, borderLeft: `4px solid ${gainPositive ? "#059669" : "#dc2626"}` }}>
            <p style={labelStyle}>Price Gain</p>
            <div style={{ ...amountStyle, color: gainPositive ? "#059669" : "#dc2626" }}>
              {gain >= 0 ? "+" : ""}{formatCurrency(gain)}
            </div>
            <div
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: gainPositive ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${gainPositive ? "#bbf7d0" : "#fecaca"}`,
                borderRadius: 99,
                padding: "3px 10px",
                marginTop: 4,
              }}
            >
              <i
                className={gainPositive ? "ri-arrow-up-line" : "ri-arrow-down-line"}
                style={{ color: gainPositive ? "#059669" : "#dc2626", fontSize: "0.85rem" }}
              />
              <span style={{ fontSize: "0.72rem", fontWeight: 700, color: gainPositive ? "#059669" : "#dc2626" }}>
                {gainPositive ? "+" : ""}{gainPct.toFixed(1)}% vs listing
              </span>
            </div>
          </div>
        )} */}

        {/* Total Collected */}
        <div style={cardBase}>
          <p style={labelStyle}>Collected</p>
          <div style={amountStyle}>{formatCurrency(totalCollected)}</div>
          <div style={{ height: 4, background: "#e2e8f0", borderRadius: 99, overflow: "hidden", marginTop: 4 }}>
            <div
              style={{
                height: "100%",
                width: `${Math.min(collectedPct, 100)}%`,
                background: "#0891b2",
                borderRadius: 99,
                transition: "width 0.4s ease",
              }}
            />
          </div>
          <p style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600, marginTop: 6, marginBottom: 0 }}>
            {collectedPct}% of sold value received
          </p>
        </div>

        {/* Remaining to Sell */}
        <div style={cardBase}>
          <p style={labelStyle}>Remaining to Sell</p>
          <div style={{ ...amountStyle, color: remainingToSell === 0 ? "#059669" : "#002045" }}>
            {formatCurrency(remainingToSell)}
          </div>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              background: remainingToSell === 0 ? "#f0fdf4" : "#fff7ed",
              border: `1px solid ${remainingToSell === 0 ? "#bbf7d0" : "#fed7aa"}`,
              borderRadius: 99,
              padding: "3px 10px",
              marginTop: 4,
            }}
          >
            <i
              className={remainingToSell === 0 ? "ri-checkbox-circle-fill" : "ri-home-4-line"}
              style={{ color: remainingToSell === 0 ? "#059669" : "#ea580c", fontSize: "0.85rem" }}
            />
            <span style={{ fontSize: "0.72rem", fontWeight: 700, color: remainingToSell === 0 ? "#059669" : "#ea580c" }}>
              {totalUnits - soldUnits} unsold unit{totalUnits - soldUnits !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevenueKpiCards;
